import { labelWords, visibleLabel } from '@/lib/mindmap-markdown';
import type { MeasureLabel } from './sideTree';

/**
 * Pill metrics per depth. These MUST match `MindmapNode`'s classes: the layout
 * reserves exactly this box, and the node is rendered at exactly this width,
 * so a drift here shows up as clipped or loose pills rather than overlaps.
 *
 * `h` is the one-line height. `lineHeight` is the font size's Tailwind
 * default (`text-sm` 20px, `text-xs` 16px), and `maxText` the widest a line
 * of text may run before the label wraps; each extra line adds a
 * `lineHeight` to `h`.
 */
export const PILL_STYLE = {
  root: {
    font: '600 14px "Inter Variable", Inter, sans-serif',
    fontPx: 14,
    padX: 16,
    h: 36,
    lineHeight: 20,
    maxText: 320,
  },
  // + 22px for the step-number badge (16px) and its margin (6px)
  branch: {
    font: '500 12px "Inter Variable", Inter, sans-serif',
    fontPx: 12,
    padX: 12,
    h: 30,
    lineHeight: 16,
    maxText: 240,
    extra: 22,
  },
  // `text-xs` is 12px (the type scale's floor): measured at 11px, every
  // leaf was reserved too narrow and its label clipped.
  leaf: {
    font: '400 12px "JetBrains Mono Variable", "JetBrains Mono", monospace',
    fontPx: 12,
    padX: 10,
    h: 26,
    lineHeight: 16,
    maxText: 220,
  },
} as const;

/** 1px border on each side. */
const BORDER = 2;
/** Room for sub-pixel differences between canvas and DOM text widths, so
 *  the browser never wraps a line the measure kept whole. Wrapped pills
 *  only: a one-line pill is `whitespace-nowrap` and cannot rewrap. */
const SLACK = 2;

/** Did the label wrap? A pill taller than its depth's one-line height. */
export function isWrapped(depth: number, h: number): boolean {
  return h > PILL_STYLE[pillKind(depth)].h;
}

export function pillKind(depth: number): keyof typeof PILL_STYLE {
  if (depth === 0) return 'root';
  if (depth === 1) return 'branch';
  return 'leaf';
}

let context: CanvasRenderingContext2D | null | undefined;

function textWidth(text: string, font: string, fontPx: number): number {
  if (context === undefined) {
    try {
      context = document.createElement('canvas').getContext('2d');
    } catch {
      context = null;
    }
  }
  if (context) {
    context.font = font;
    const width = context.measureText(text).width;
    // jsdom's canvas stub returns 0; fall through to the estimate.
    if (width > 0) return width;
  }
  return text.length * fontPx * 0.58;
}

/**
 * Greedy word wrap, as the browser does it: words go on a line until the
 * next would pass `max`. A word wider than `max` on its own is broken across
 * lines (`break-words`). Returns the line count and the widest line.
 */
export function wrapLines(
  wordWidths: number[],
  spaceWidth: number,
  max: number
): { lines: number; width: number } {
  let lines = 0;
  let width = 0;
  let current = -1; // width of the open line; -1 while none is open
  for (const word of wordWidths) {
    if (current >= 0 && current + spaceWidth + word <= max) {
      current += spaceWidth + word;
    } else {
      if (current >= 0) width = Math.max(width, current);
      const cut = Math.max(1, Math.ceil(word / max));
      lines += cut;
      current = cut > 1 ? max : word;
    }
  }
  if (current >= 0) width = Math.max(width, current);
  return { lines: Math.max(1, lines), width };
}

/**
 * Measure a label as its pill will render it. Canvas `measureText` at the real
 * font, so layout is one pass with no render-then-measure flash; the caller
 * waits for `document.fonts.ready` so the numbers are for the loaded face.
 * Maths is measured as the text KaTeX shows, not its TeX source, and a long
 * label wraps onto more lines, growing the pill downward rather than wider.
 */
export const measurePill: MeasureLabel = (label, depth) => {
  const style = PILL_STYLE[pillKind(depth)];
  const measure = (text: string) => textWidth(text, style.font, style.fontPx);
  const extra = 'extra' in style ? style.extra : 0;
  const words = labelWords(label).map(word => measure(visibleLabel(word)));
  const oneLine = measure(visibleLabel(label));
  const { lines, width } =
    oneLine <= style.maxText
      ? { lines: 1, width: oneLine }
      : wrapLines(words, measure(' '), style.maxText);
  const slack = lines > 1 ? SLACK : 0;
  const w = Math.ceil(width + slack + style.padX * 2 + extra + BORDER);
  return { w, h: style.h + (lines - 1) * style.lineHeight };
};
